import { ReactElement, useMemo, useState } from "react"
import BaseTable, { Column, ColumnShape, SortOrder } from "react-base-table"
import { AutoSizer } from "react-virtualized"

import { faEye, faPen, faTrash } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Checkbox } from "@wfp/ui"

import { IReportObs, ObservationStatus, ValidationTableProps } from "../../../../interfaces/markets"

import { MARKET_MODULE } from "../../../../constants/constants"
import { formatDate } from "../../../../utils/helpers"
import { getInvalidKeyClassName, getOutlierClassName, isItemUpdated } from "../../../../utils/validation"

import PermissionWrapper from "../../../Shared/Common/PermissionWrapper"

const permissions = {
  moduleId: MARKET_MODULE,
  subModule: "reports",
}

const ValidationTable = ({
  data,
  onEdit,
  onDetails,
  onDelete,
  selectable = false,
  selectedItems = [],
  onSelectionChange,
  updatedItems,
  extraColumns = [],
  fullscreen = false,
  columnsKey = "",
}: ValidationTableProps): ReactElement => {
  const [sortBy, setSortBy] = useState<{ key: string; order: SortOrder }>({
    key: "marketName",
    order: "asc",
  })

  const handleSelectChange = (selected: boolean, id: number): void => {
    if (!onSelectionChange) return

    if (selected) {
      onSelectionChange([...selectedItems, id])
    } else {
      onSelectionChange(selectedItems.filter(itemId => itemId !== id))
    }
  }

  const handleSelectAll = (checked: boolean): void => {
    if (!onSelectionChange) return

    if (checked) {
      const allIds = data.map(row => row.commodityPriceObservationId)
      const newIds = allIds.filter(id => !selectedItems.includes(id))
      onSelectionChange([...selectedItems, ...newIds])
    } else {
      const allIds = data.map(row => row.commodityPriceObservationId)
      onSelectionChange(selectedItems.filter(id => !allIds.includes(id)))
    }
  }

  const handleColumnSort = (sortByInfo: { key: string; order: SortOrder }): void => {
    setSortBy(sortByInfo)
  }

  const sortedData = useMemo((): IReportObs[] => {
    if (!sortBy.key) return data

    const sorted = [...data].sort((a, b) => {
      const aValue = a[sortBy.key as keyof IReportObs]
      const bValue = b[sortBy.key as keyof IReportObs]

      if (aValue === null || aValue === undefined) return 1
      if (bValue === null || bValue === undefined) return -1

      if (typeof aValue === "string" && typeof bValue === "string") {
        return aValue.localeCompare(bValue)
      }

      if (aValue < bValue) return -1
      if (aValue > bValue) return 1
      return 0
    })

    return sortBy.order === "desc" ? sorted.reverse() : sorted
  }, [data, sortBy])

  const columns: ColumnShape<IReportObs>[] = [
    ...(selectable
      ? [
          {
            width: 40,
            key: "selection",
            cellRenderer: ({ rowData }) => {
              const isChecked = selectedItems.includes(rowData.commodityPriceObservationId)
              return (
                <Checkbox
                  id={rowData.commodityPriceObservationId.toString()}
                  labelText=""
                  checked={isChecked}
                  wrapperClassName="!m-0"
                  onChange={e => handleSelectChange(e.target.checked, rowData.commodityPriceObservationId)}
                />
              )
            },
            headerRenderer: () => {
              const allSelected =
                data.length > 0 && data.every(row => selectedItems.includes(row.commodityPriceObservationId))
              return (
                <Checkbox
                  id="select-all"
                  labelText=""
                  checked={allSelected}
                  wrapperClassName="!m-0"
                  onChange={e => handleSelectAll(e.target.checked)}
                />
              )
            },
          },
        ]
      : []),
    {
      key: "commodityPriceDate",
      title: "Date",
      width: 130,
      sortable: true,
      cellRenderer: ({ rowData }: { rowData: IReportObs }) => formatDate(rowData.commodityPriceDate, "DD-MMM-YYYY"),
    },
    {
      key: "marketName",
      dataKey: "marketName",
      title: "Market",
      width: 120,
      flexGrow: 2,
      resizable: true,
      sortable: true,
      className: ({ rowData }: { rowData: IReportObs }) => getInvalidKeyClassName(rowData, "marketId"),
    },
    {
      key: "commodityName",
      dataKey: "commodityName",
      title: "Commodity",
      width: 120,
      flexGrow: 1,
      resizable: true,
      sortable: true,
      className: ({ rowData }: { rowData: IReportObs }) => getInvalidKeyClassName(rowData, "commodityId"),
    },
    {
      key: "priceTypeName",
      dataKey: "priceTypeName",
      title: "Price Type",
      width: 100,
      resizable: true,
      sortable: true,
      className: ({ rowData }: { rowData: IReportObs }) => getInvalidKeyClassName(rowData, "priceTypeId"),
    },
    {
      key: "commodityUnitName",
      dataKey: "commodityUnitName",
      title: "Unit",
      width: 80,
      resizable: true,
      sortable: true,
      className: ({ rowData }: { rowData: IReportObs }) => getInvalidKeyClassName(rowData, "commodityUnitId"),
    },
    {
      key: "currencyName",
      dataKey: "currencyName",
      title: "Currency",
      width: 80,
      resizable: true,
      sortable: true,
      className: ({ rowData }: { rowData: IReportObs }) => getInvalidKeyClassName(rowData, "currencyId"),
    },
    {
      key: "commodityPriceValue",
      dataKey: "commodityPriceValue",
      title: "Price",
      width: 100,
      resizable: true,
      sortable: true,
      cellRenderer: ({ rowData }: { rowData: IReportObs }) => rowData.commodityPriceValue?.toLocaleString(),
      className: ({ rowData }: { rowData: IReportObs }) => getOutlierClassName(rowData),
    },
    {
      key: "commodityPriceSourceName",
      dataKey: "commodityPriceSourceName",
      title: "Source",
      resizable: true,
      sortable: true,
      width: 80,
    },
    ...extraColumns,
    {
      key: "action",
      width: 75,
      align: Column.Alignment.RIGHT,
      maxWidth: 75,
      hidden: !(onEdit || onDetails),
      flexGrow: 0,
      minWidth: 50,
      cellRenderer: ({ rowData }) => (
        <div
          aria-hidden
          className="d-flex align-items-center justify-end gap-2 pe-1"
          onClick={e => {
            e.preventDefault()
            e.stopPropagation()
          }}
        >
          {rowData.observationStatusId !== ObservationStatus.APPROVED && (
            <>
              {onDetails && (
                <PermissionWrapper {...permissions} feature="details">
                  <FontAwesomeIcon
                    icon={faEye}
                    onClick={() => onDetails(rowData)}
                    color="var(--interactive-01)"
                    title="Details"
                    className="cursor-pointer"
                  />
                </PermissionWrapper>
              )}

              {onEdit && (
                <PermissionWrapper {...permissions} feature="edit">
                  <FontAwesomeIcon
                    icon={faPen}
                    onClick={() => onEdit(rowData)}
                    color="var(--support-02)"
                    title="Edit"
                    className="cursor-pointer"
                  />
                </PermissionWrapper>
              )}

              {onDelete && (
                <PermissionWrapper {...permissions} feature="delete">
                  <FontAwesomeIcon
                    icon={faTrash}
                    onClick={() => onDelete(rowData)}
                    color="var(--support-01)"
                    title="Delete"
                    className="cursor-pointer"
                  />
                </PermissionWrapper>
              )}
            </>
          )}
        </div>
      ),
    },
  ]

  return (
    <div
      className="h-full w-full"
      style={{
        minHeight: "calc(100vh - 520px)",
        maxHeight: "calc(100vh - 210px)",
        height: fullscreen ? "calc(100vh - 260px)" : "calc(100vh - 520px)",
      }}
    >
      <AutoSizer>
        {({ width, height }) => (
          <BaseTable
            key={`${selectedItems.join(",")}-${columnsKey}`}
            data={sortedData}
            width={width}
            height={height}
            rowKey="commodityPriceObservationId"
            rowHeight={35}
            headerHeight={35}
            columns={columns}
            emptyRenderer={() => <p>No records to display</p>}
            className="custom-table text-sm"
            sortBy={sortBy}
            onColumnSort={item => handleColumnSort({ key: item.key as keyof IReportObs, order: item.order })}
            rowClassName={({ rowData }) => {
              if (isItemUpdated(rowData.commodityPriceObservationId, updatedItems)) {
                return "!bg-green-100"
              }
              if (rowData.observationStatusId === ObservationStatus.APPROVED) {
                return "!bg-gray-50 !text-gray-400 !italic"
              }
              return ""
            }}
          />
        )}
      </AutoSizer>
    </div>
  )
}

export default ValidationTable
